import React, { useCallback } from 'react';
import clsx from 'clsx';
import { Nullable, SelectOption } from 'global';
import { useForm, useWatch } from 'react-hook-form';
import { AttachMoney } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { AutocompleteField, CurrencyInputField, DateField, InputField, InterestInputField } from 'components/form';
import { calculateCommissionFlat, calculateCommissionPercent } from 'utils/financeCommissionCalculations';
import { DateFormats } from '../../../../constants/common';

export interface FinanceProgramMarkup {
  flat: Nullable<number>;
  percent: Nullable<number>;
}

export interface FundingCardFormValues {
  lenderName: string;
  financeProgram: Nullable<SelectOption>;
  financeProgramName: Nullable<string>;
  dateFunded: Nullable<string>;
  fundedAmount: Nullable<number>;
  financeMarkupFlat: Nullable<number>;
  commissionReceivedDate: Nullable<string>;
  financeMarkupPercentage: Nullable<number>;
  financeCommissionFlat: Nullable<number>;
  financeCommissionPercent: Nullable<number>;
  fundedSubmission: Nullable<SelectOption & { dynamicCSId: string }>;
}

interface FundingCardLoadings {
  isFPMarkupLoading: boolean;
  isCSStatusUpdating: boolean;
  isFinanceProgramsLoading: boolean;
  isCreditSubmissionsLoading: boolean;
  isCreditSubmissionRevenueLoading: boolean;
}

export interface CreditSubmissionRevenue {
  lenderName: string;
  approvedAmount: Nullable<number>;
  fundedTimestamp: Nullable<string>;
  commissionReceivedDate: Nullable<string>;
  financeCommissionPercent: Nullable<number>;
}

interface FundingCardActions {
  getInitialFundingCardFormValues: () => Promise<FundingCardFormValues>;
  handleGetSubmissionRevenue: (dynamicsId: string) => Promise<Nullable<CreditSubmissionRevenue>>;
  onUpdateCreditSubmission: (
    id: string,
    dto: { commissionReceivedDate: Nullable<string>; financeCommissionPercent: Nullable<number> }
  ) => Promise<void>;
  handleFetchFinanceProgramMarkup: (financeProgramId: string) => Promise<Nullable<FinanceProgramMarkup>>;
}

export interface FundingCardProps extends FundingCardActions {
  loading: FundingCardLoadings;
  financeProgramsOptions: SelectOption[];
  fundedSubmissionOptions: Array<SelectOption & { dynamicCSId: string }>;
  onFormStateChange?: (
    isDirty: boolean,
    isDisabled: boolean,
    handleSave: () => Promise<void>,
    formValues: FundingCardFormValues
  ) => void;
}

export const MANUAL_ENTRY_OPTION = { value: 'manual', label: 'Manual entry', dynamicCSId: '' };

export const FundingCard = ({
  loading,
  financeProgramsOptions,
  fundedSubmissionOptions,
  onUpdateCreditSubmission,
  handleGetSubmissionRevenue,
  handleFetchFinanceProgramMarkup,
  getInitialFundingCardFormValues,
  onFormStateChange,
}: FundingCardProps) => {
  const {
    control,
    setValue,
    getValues,
    formState: { isDirty, defaultValues },
  } = useForm<FundingCardFormValues>({ defaultValues: getInitialFundingCardFormValues });

  const fundedSubmissionValue = useWatch({ control, name: 'fundedSubmission' });
  const isManualEntry = fundedSubmissionValue?.value === MANUAL_ENTRY_OPTION.value;
  const lenderName = useWatch({ control, name: 'lenderName' });
  const financeCommissionFlat = useWatch({ control, name: 'financeCommissionFlat' });
  const financeCommissionPercent = useWatch({ control, name: 'financeCommissionPercent' });
  const financeProgramValue = useWatch({ control, name: 'financeProgram' });
  const financeMarkupFlat = useWatch({ control, name: 'financeMarkupFlat' });
  const financeMarkupPercentage = useWatch({ control, name: 'financeMarkupPercentage' });
  const commissionReceivedDate = useWatch({ control, name: 'commissionReceivedDate' });

  const handleUpdateCS = useCallback(
    async (dynamicCSId: string) => {
      await onUpdateCreditSubmission(dynamicCSId, {
        commissionReceivedDate: getValues('commissionReceivedDate'),
        financeCommissionPercent: getValues('financeCommissionPercent'),
      });
    },
    [onUpdateCreditSubmission, getValues]
  );

  const calcCommissionsFlat = (commissionPercent: number) => {
    const { fundedAmount } = getValues();

    if (!fundedAmount) return;

    setValue('financeCommissionFlat', calculateCommissionFlat(fundedAmount, commissionPercent), { shouldDirty: true });
  };

  const calcCommissionsPercent = (commissionFlat: number) => {
    const { fundedAmount } = getValues();

    if (!fundedAmount) return;

    setValue('financeCommissionPercent', calculateCommissionPercent(fundedAmount, commissionFlat), {
      shouldDirty: true,
    });
  };

  const calcMarkupFlat = (markupPercent: number) => {
    const { fundedAmount } = getValues();

    if (!fundedAmount) return;

    setValue('financeMarkupFlat', calculateCommissionFlat(fundedAmount, markupPercent), { shouldDirty: true });
  };

  const calcMarkupPercent = (markupFlat: number) => {
    const { fundedAmount } = getValues();

    if (!fundedAmount) return;

    setValue('financeMarkupPercentage', calculateCommissionPercent(fundedAmount, markupFlat), { shouldDirty: true });
  };

  const isSubmitBtnDisabled = !isDirty || !fundedSubmissionValue || loading.isCreditSubmissionRevenueLoading;

  const handleSaveCS = useCallback(async () => {
    if (fundedSubmissionValue && !isManualEntry) await handleUpdateCS(fundedSubmissionValue.dynamicCSId);
  }, [fundedSubmissionValue, isManualEntry, handleUpdateCS]);

  React.useEffect(() => {
    if (onFormStateChange) {
      const formValues = getValues();
      onFormStateChange(isDirty, isSubmitBtnDisabled, handleSaveCS, formValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDirty,
    isSubmitBtnDisabled,
    onFormStateChange,
    fundedSubmissionValue,
    lenderName,
    financeCommissionFlat,
    financeCommissionPercent,
    financeProgramValue,
    financeMarkupFlat,
    financeMarkupPercentage,
    commissionReceivedDate,
  ]);

  return (
    <Box
      className={clsx(
        'revenueCard',
        (loading.isFPMarkupLoading || loading.isCreditSubmissionRevenueLoading) && 'revenueCard-loading'
      )}
    >
      <Box className="revenueCardHeader">
        <Box className="revenueCardHeaderIconWithTitle">
          <Box className="revenueCardHeaderIcon">
            <AttachMoney />
          </Box>

          <Typography variant="subtitle1" component="span">
            Finance
          </Typography>
        </Box>
      </Box>

      <Box>
        <Stack gap="1rem">
          <Stack direction="row" gap="1rem">
            <AutocompleteField
              control={control}
              name="fundedSubmission"
              options={[...fundedSubmissionOptions, MANUAL_ENTRY_OPTION]}
              isLoading={loading.isCreditSubmissionsLoading}
              textFieldProps={{ variant: 'outlined', label: 'Submission', size: 'small' }}
              extraAction={async nextValue => {
                setValue('lenderName', '', { shouldDirty: false });
                setValue('dateFunded', null, { shouldDirty: false });
                setValue('fundedAmount', null, { shouldDirty: false });
                setValue('commissionReceivedDate', null, { shouldDirty: false });
                setValue('financeCommissionPercent', null, { shouldDirty: false });
                setValue('financeCommissionFlat', null, { shouldDirty: false });
                setValue('financeProgramName', null, { shouldDirty: false });
                setValue('financeProgram', null, { shouldDirty: false });
                setValue('financeMarkupFlat', null, { shouldDirty: false });
                setValue('financeMarkupPercentage', null, { shouldDirty: false });

                if (nextValue?.value === MANUAL_ENTRY_OPTION.value) return;

                if (nextValue?.value && defaultValues) {
                  const response = await handleGetSubmissionRevenue(nextValue.value);

                  if (!response) return;

                  setValue('lenderName', response.lenderName, { shouldDirty: false });
                  setValue('dateFunded', response.fundedTimestamp, { shouldDirty: false });
                  setValue('fundedAmount', response.approvedAmount, { shouldDirty: false });
                  setValue('commissionReceivedDate', response.commissionReceivedDate, { shouldDirty: false });
                  setValue('financeCommissionPercent', response.financeCommissionPercent, { shouldDirty: false });

                  if (response.approvedAmount && response.financeCommissionPercent) {
                    const financeCommissionFlat = calculateCommissionFlat(
                      response.approvedAmount,
                      response.financeCommissionPercent
                    );
                    setValue('financeCommissionFlat', financeCommissionFlat, { shouldDirty: false });
                  } else {
                    setValue('financeCommissionFlat', null, { shouldDirty: false });
                  }
                }
              }}
            />
          </Stack>

          <Stack direction="row" gap="1rem">
            <InputField
              label="Lender"
              name="lenderName"
              control={control}
              inputProps={{ variant: 'outlined', disabled: !isManualEntry }}
            />

            {isManualEntry ? (
              <InputField
                control={control}
                name="financeProgramName"
                label="Finance Program Name"
                inputProps={{ variant: 'outlined', size: 'small' }}
              />
            ) : (
              <AutocompleteField
                control={control}
                name="financeProgram"
                options={financeProgramsOptions}
                isLoading={loading.isFinanceProgramsLoading}
                textFieldProps={{ variant: 'outlined', label: 'Finance Program', size: 'small' }}
                extraAction={async nextValue => {
                  if (nextValue?.value) {
                    const response = await handleFetchFinanceProgramMarkup(nextValue.value);

                    if (!response) return;

                    setValue('financeMarkupFlat', response.flat);
                    setValue('financeMarkupPercentage', response.percent);
                  } else {
                    setValue('financeMarkupFlat', defaultValues?.financeMarkupFlat ?? null);
                    setValue('financeMarkupPercentage', defaultValues?.financeMarkupPercentage ?? null);
                  }
                }}
              />
            )}
          </Stack>

          <Stack direction="row" gap="1rem">
            <Stack direction="row" gap="1rem" width="100%">
              <CurrencyInputField
                control={control}
                name="fundedAmount"
                label="Funded Amount"
                textFieldProps={{ variant: 'outlined', disabled: !isManualEntry }}
              />
            </Stack>

            <Stack direction="row" gap="1rem" width="100%">
              <CurrencyInputField
                control={control}
                label="Finance Markup ($)"
                name="financeMarkupFlat"
                textFieldProps={{ variant: 'outlined', disabled: !isManualEntry }}
                customOnChange={(nextValue, name) => {
                  setValue(name, nextValue, { shouldDirty: true });
                  calcMarkupPercent(nextValue);
                }}
              />
              <InterestInputField
                control={control}
                label="Finance Markup (%)"
                name="financeMarkupPercentage"
                textFieldProps={{ variant: 'outlined', disabled: !isManualEntry }}
                customOnChange={(nextValue, name) => {
                  setValue(name, nextValue, { shouldDirty: true });
                  calcMarkupFlat(nextValue);
                }}
              />
            </Stack>
          </Stack>

          <Stack direction="row" gap="1rem">
            <DateField
              disabled={!isManualEntry}
              name="dateFunded"
              control={control}
              label="Date Funded"
              format={DateFormats.Short}
              slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
            />

            <DateField
              control={control}
              name="commissionReceivedDate"
              label="Commission Received Date"
              format={DateFormats.Short}
              slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
            />
          </Stack>

          <Stack direction="row" gap="1rem">
            <InterestInputField
              control={control}
              label="Finance Commission (%)"
              name="financeCommissionPercent"
              textFieldProps={{ variant: 'outlined', disabled: !fundedSubmissionValue }}
              customOnChange={(nextValue, name) => {
                setValue(name, nextValue, { shouldDirty: true });
                calcCommissionsFlat(nextValue);
              }}
            />
            <CurrencyInputField
              control={control}
              name="financeCommissionFlat"
              label="Finance Commission ($)"
              textFieldProps={{ variant: 'outlined', disabled: !fundedSubmissionValue }}
              customOnChange={(nextValue, name) => {
                setValue(name, nextValue, { shouldDirty: true });
                calcCommissionsPercent(nextValue);
              }}
            />
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};
